import { useEffect } from 'react';
import { create, StoreApi, useStore } from 'zustand';

interface PriorityDialogStore {
  map: Map<string, number>;
  lastItem: { id: string; priority: number } | undefined;
  push: (id: string, priority: number) => void;
  deleteItem: (id: string) => void;
}

let queueStore: StoreApi<PriorityDialogStore>;

const getPriorityDialogStore = () => {
  if (queueStore) {
    return queueStore;
  }

  queueStore = create<PriorityDialogStore>()((set, get) => {
    const getHighestPriority = () => {
      return Array.from(get().map.entries()).reduce(
        (highest, [id, priority]) => {
          if (!highest) {
            return { id, priority };
          }

          if (priority > highest.priority) {
            return { id, priority };
          }

          return highest;
        },
        undefined as
          | {
              id: string;
              priority: number;
            }
          | undefined
      );
    };
    return {
      map: new Map<string, number>(),
      lastItem: undefined,
      push: (id, priority) => {
        get().map.set(id, priority);
        set({ lastItem: getHighestPriority() });
      },
      deleteItem: (id) => {
        get().map.delete(id);
        set({ lastItem: getHighestPriority() });
      },
    };
  });
  return queueStore;
};

export const ModalPriority = {
  STEPUP: 3000,
  AUTOCLAIM: 2000,
  INTERSTITIAL: 1100,
  SURVEY: 1000,
};

export const usePriorityModal = ({
  id,
  priority,
  open,
}: {
  id: string;
  priority: number;
  open: boolean;
}) => {
  const { lastItem } = useStore(getPriorityDialogStore(), ({ lastItem }) => ({
    lastItem,
  }));
  useEffect(() => {
    if (open) {
      getPriorityDialogStore().getState().push(id, priority);
    } else {
      getPriorityDialogStore().getState().deleteItem(id);
    }

    return () => {
      getPriorityDialogStore().getState().deleteItem(id);
    };
  }, [open, id]);

  return {
    visible: open && lastItem?.id === id,
  };
};
